import { Tooltip } from "@blueprintjs/core"
import { dateHelpers } from "@runn/calculations"
import { format } from "date-fns"
import React, { useMemo } from "react"
import { useFragment } from "react-relay"
import { graphql } from "react-relay"

import styles from "./ContractsTooltip.module.css"

import { ContractsTooltip_person$key } from "./__generated__/ContractsTooltip_person.graphql"
import { PersonSidePanel_user$key } from "~/Planner/PersonSidePanel/__generated__/PersonSidePanel_user.graphql"

import { track } from "~/helpers/analytics"

import { useSidePanel } from "~/common/SidePanel/SidePanel"
import InfoOutline from "~/common/react-icons/infoOutline"

import { usePermissions } from "~/Permissions/usePermissions"
import PersonSidePanel from "~/Planner/PersonSidePanel/PersonSidePanel"

type Contract = {
  start_date: string
  end_date?: string
}

type Props = {
  user: PersonSidePanel_user$key
  person: ContractsTooltip_person$key
  disableContractIconClick: boolean
}

const formatDate = (dateString?: string) => {
  if (!dateString) return null

  const date = dateHelpers.parseRunnDate(dateString)
  return format(date, "d MMM yyyy")
}

const ContractTooltipContent = ({
  contracts,
}: {
  contracts: ReadonlyArray<Contract>
}) => {
  return (
    <div className={styles.popoverContainer}>
      {contracts.map((contract, idx) => (
        <div className={styles.contractsContainer}>
          <span className={styles.contractTitle}>Upcoming Contract</span>
          <div className={styles.contract} key={`contract-${idx}`}>
            {formatDate(contract.start_date)}
            {contract.end_date
              ? ` - ${formatDate(contract.end_date)}`
              : " - no end date"}
          </div>
        </div>
      ))}
    </div>
  )
}

const ContractsTooltip = ({
  user,
  disableContractIconClick,
  ...props
}: Props) => {
  const { openPanel, closePanel, isOpen } = useSidePanel()

  const person = useFragment(
    graphql`
      fragment ContractsTooltip_person on people {
        id
        contracts {
          id
          start_date: start_date_runn
          end_date: end_date_runn
        }
        ...PersonSidePanel_person
      }
    `,
    props.person,
  )
  const { contracts } = person

  const { can, subject } = usePermissions()
  const canViewPerson = can("view", subject("Person", person))

  const upcomingContracts = useMemo(() => {
    return contracts.filter((contract) => {
      return contract.start_date > dateHelpers.formatToRunnDate(new Date())
    })
  }, [contracts])

  if (!upcomingContracts || upcomingContracts.length === 0 || !canViewPerson) {
    return null
  }

  const handleClick = () => {
    if (disableContractIconClick) {
      return
    }

    if (isOpen) {
      closePanel()
      return
    }
    track("Panel Opened", { type: "Person Side Panel", tab: "Contract" })
    openPanel(
      <PersonSidePanel user={user} person={person} initialTab="contract" />,
    )
  }

  return (
    <Tooltip
      lazy
      content={<ContractTooltipContent contracts={upcomingContracts} />}
      className={styles.tooltip}
    >
      <InfoOutline
        style={{ cursor: disableContractIconClick ? "default" : "pointer" }}
        onClick={handleClick}
      />
    </Tooltip>
  )
}

export default ContractsTooltip
